import React from "react";

import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import { PlatformName } from "@common/models";
import { isAdmin } from "@common/models/Member.model";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import ApiKeysCard from "@main/pages/Administration/components/ApiKeysCard/ApiKeysCard";
import { DeleteAppCard } from "@main/pages/Administration/components/AppsCards/DeleteAppCard/DeleteAppCard";
import { EssentialsCard } from "@main/pages/Administration/components/AppsCards/EssentialsCard/EssentialsCard";
import { RulesCard } from "@main/pages/Administration/components/AppsCards/RulesCard/RulesCard";
import SheldonCard from "@main/pages/Administration/components/AppsCards/SheldonCard";
import StatusCard from "@main/pages/Administration/components/AppsCards/StatusCard";
import NoAppsView from "@main/pages/Administration/components/NoAppsView";
import useAppsGeneralViewConsumer from "@main/pages/Administration/consumers/useAppsGeneralViewConsumer";
import PermittedUrlsCard from "main/pages/Administration/components/AppsCards/DomainsCard/PermittedUrlsCard";

export default function AppsGeneralView(): JSX.Element {
  const { loading: appsLoading } = useAppContext();
  const { selectedWorkspace } = useAppSelectionContext();

  const {
    updateApp,
    activateInactivateApp,
    deleteApp,
    saving,
    allApps,
    localSelectedApp,
    updateAppLogo,
    deleteAppLogo,
    rules,
    refetchRules,
    deleteRule,
    addApiKey,
    deleteApiKey,
    addAppDomain,
    deleteAppDomain,
    setSheldonEnabled,
    loading,
  } = useAppsGeneralViewConsumer();

  if (!appsLoading && allApps.length === 0) {
    return (
      <PageWrap style={{ marginTop: "4rem" }}>
        <NoAppsView selectedWorkspace={selectedWorkspace} />
      </PageWrap>
    );
  }

  if (!localSelectedApp) {
    return <Spinner />;
  }

  const localLoading = appsLoading || loading;

  return (
    <PageWrap style={{ marginTop: "4rem" }}>
      <StatusCard
        isAppActive={localSelectedApp.active}
        handleActiveChange={() => activateInactivateApp(localSelectedApp.active)}
        loading={appsLoading}
        saving={saving}
      />
      <EssentialsCard
        key={localSelectedApp.id}
        selectedApp={localSelectedApp}
        appName={localSelectedApp.name}
        updateApp={updateApp}
        loading={appsLoading}
        saving={saving}
        updateAppLogo={updateAppLogo}
        deleteAppLogo={deleteAppLogo}
        uploading={saving}
      />
      {localSelectedApp.platform.name === PlatformName.WEB && (
        <PermittedUrlsCard
          app={localSelectedApp}
          loading={appsLoading}
          addAppDomain={addAppDomain}
          deleteAppDomain={deleteAppDomain}
          selectedWorkspace={selectedWorkspace}
        />
      )}
      <ApiKeysCard
        app={localSelectedApp}
        loading={appsLoading}
        addApiKey={addApiKey}
        deleteApiKey={deleteApiKey}
        selectedWorkspace={selectedWorkspace}
      />
      <SheldonCard
        isSheldonEnabled={localSelectedApp.ai_enabled}
        setSheldonEnabled={setSheldonEnabled}
        loading={appsLoading}
        saving={saving}
      />
      <RulesCard
        selectedApp={localSelectedApp}
        loading={localLoading}
        rules={rules}
        getRules={refetchRules}
        deleteRule={deleteRule}
      />
      <DeleteAppCard
        disabled={!isAdmin(selectedWorkspace?.role)}
        app={localSelectedApp}
        deleteApp={deleteApp}
        loading={localLoading}
      />
    </PageWrap>
  );
}
